/**
 * What the planner prices for the two drafts the cost guide quotes.
 *
 * Both drafts are the builder's fresh defaults on the intl market: the
 * one-room starter (2.5 × 2.0 m) and the two-room Lakeside preset
 * (3.0 × 4.0 m, the same draft as the landing film). Figures are copied here
 * rather than computed so the guide does not ship the engines;
 * `costFacts.test.tsx` rebuilds both drafts through the store and the
 * builder's own derived-data hook and fails when any figure drifts.
 *
 * Amounts are SEK at the planner's baseline prices; format per market at
 * render.
 */

export type CostBlock =
	| "foundation"
	| "frame"
	| "roofExterior"
	| "insulation"
	| "interior"
	| "heater"
	| "openings"
	| "deck";

export interface CostDraftFacts {
	widthMm: number;
	lengthMm: number;
	/** Rows on the shopping tab. */
	purchaseRows: number;
	/** Material total, SEK. */
	materialCostSek: number;
	bySek: Record<CostBlock, number>;
	/** The single most expensive shopping-list row. */
	topItem: { materialKey: string; sek: number };
	heaterSek: number;
	/** Interior wood the draft opens with. */
	interiorWood: "asp" | "al" | "gran";
	/** Planner's price change, SEK, for switching interior wood. */
	interiorWoodDeltaSek: Record<"asp" | "al" | "gran", number>;
}

export const COST_FACTS: { oneRoom: CostDraftFacts; lakeside: CostDraftFacts } =
	{
		oneRoom: {
			widthMm: 2500,
			lengthMm: 2000,
			purchaseRows: 51,
			materialCostSek: 74_860,
			bySek: {
				foundation: 3_943,
				frame: 3_257,
				roofExterior: 11_497,
				insulation: 4_078,
				interior: 21_122,
				heater: 21_088,
				openings: 9_875,
				deck: 0,
			},
			topItem: { materialKey: "interior.sauna-panel", sek: 12_760 },
			heaterSek: 8_500,
			interiorWood: "asp",
			interiorWoodDeltaSek: { asp: 0, al: -4_044, gran: -11_932 },
		},
		lakeside: {
			widthMm: 3000,
			lengthMm: 4000,
			purchaseRows: 72,
			materialCostSek: 142_247,
			bySek: {
				foundation: 7_296,
				frame: 5_952,
				roofExterior: 24_091,
				insulation: 6_638,
				interior: 39_051,
				heater: 19_993,
				openings: 33_170,
				deck: 6_056,
			},
			topItem: { materialKey: "systems.panorama-glass", sek: 18_300 },
			heaterSek: 8_500,
			interiorWood: "al",
			interiorWoodDeltaSek: { asp: 9_208, al: 0, gran: -17_904 },
		},
	};

/**
 * Which cost block a shopping-list row belongs to, by its material key, with
 * the row's build phase as the fallback for unkeyed rows.
 */
export function costBlockOf(row: {
	materialKey?: string | null;
	phase?: string | null;
}): CostBlock {
	const key = row.materialKey ?? "";
	if (key.startsWith("deck.") || row.phase === "deck") return "deck";
	if (key.startsWith("foundation.")) return "foundation";
	if (key.startsWith("roof.") || key.startsWith("exterior.")) {
		return "roofExterior";
	}
	if (
		key === "interior.insulation" ||
		key === "interior.vapor-barrier" ||
		key === "interior.foil-tape"
	) {
		return "insulation";
	}
	if (key.startsWith("interior.")) return "interior";
	if (
		key.startsWith("heater.") ||
		key.startsWith("chimney.") ||
		key === "systems.heat-shield"
	) {
		return "heater";
	}
	if (key.startsWith("framing.") || key.startsWith("partition.")) {
		return "frame";
	}
	switch (row.phase) {
		case "foundation":
			return "foundation";
		case "framing":
			return "frame";
		case "roofExterior":
			return "roofExterior";
		case "interior":
			return "interior";
		default:
			return "openings";
	}
}
